import { useState, useEffect } from 'react'
import { Loader2, Send, Edit2, X, Bot, Shield, CornerUpLeft } from 'lucide-react'
import { Modal } from '../ui/Modal'
import { useAuth } from '../../contexts/AuthContext'
import { useToast } from '../../contexts/ToastContext'
import { logJanetDecision } from '../../hooks/useJanet'
import { useCompose } from '../../contexts/ComposeContext'
import JanetDraftReview from './JanetDraftReview'
import { buildReplyPlan, recipientsToEmails } from '../../lib/replyContext'

const JANET_SIG_MARKER = '<br><div style="margin-top:16px;border-top:1px solid #e5e7eb;padding-top:12px">'
function stripJanetSig(html) {
  if (!html) return ''
  const idx = html.indexOf(JANET_SIG_MARKER)
  return idx >= 0 ? html.slice(0, idx) : html
}

// ─── Draft type labels ────────────────────────────────────────────────────────

const DRAFT_TYPE_LABELS = {
  scheduling:        'Scheduling Reply',
  submission_intro:  'Submission Intro',
  follow_up:         'Follow-Up',
  logistics:         'Logistics / Confirmation',
  connection_offer:  'Connection Offer',
  negotiation_reply: 'Negotiation Reply',
  introduction:      'Introduction',
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export default function JanetDraftModal({
  isOpen,
  onClose,
  // What to draft
  draftType = 'follow_up',
  voice,                    // 'principal' (Aaron's voice, 1st person) | 'janet' (default, EA voice)
  // Who Janet is acting for
  principal,                // { name, email } — defaults to current user
  // Who Janet is writing to
  contact,                  // { name, email, cp_tier, id } — primary recipient
  additionalRecipients,     // [{ name, email, id }] — for introductions (2nd party, etc.)
  // Context
  context,                  // incoming email body or request description
  contextSource,            // 'external_email' when context is a raw inbound email body — janet-draft injection-fences it
  subject,                  // original subject
  offeredPersonName,        // connection_offer: name of the person being offered as a connection
  // Replying in-thread
  replyTo,                  // { gmailMessageId, gmailThreadId, subject, fromName, fromEmail,
                            //   sentAt, toEmails[], ccEmails[], bodyHtml, bodyText, source }
                            // When present the draft is sent as a REPLY ALL on that message's
                            // thread instead of as a new email. See src/lib/replyContext.js.
  dealContext,              // optional plain-text terms/state block for negotiation replies
  // Callbacks
  onSent,                   // called after human hits Send
}) {
  const { user, profile } = useAuth()
  const toast   = useToast()
  const { openCompose } = useCompose()

  const effectivePrincipal = principal || {
    name:  profile?.full_name || user?.email?.split('@')[0] || 'Aaron',
    email: user?.email || '',
  }

  // Default to principal voice — Janet drafts in the user's voice unless explicitly asked
  // to send as herself ("have Janet send..."). The voice decides who the email is FROM.
  const effectiveVoice = voice || 'principal'
  const sendFromJanet  = effectiveVoice === 'janet'
  const fromAddress    = sendFromJanet ? 'janet@chatrone.com' : effectivePrincipal.email

  // Reply plan — when the caller handed us a message to answer, the draft goes
  // back on THAT thread, to everyone who was on it. Janet's own address is
  // excluded alongside the principal's so she never Ccs herself.
  const replyPlan = buildReplyPlan(replyTo, {
    exclude: [effectivePrincipal.email, user?.email, 'janet@chatrone.com'].filter(Boolean),
  })
  const isReply = !!replyPlan

  const [stage, setStage]               = useState('input')   // input | generating | review
  const [extraInstructions, setExtra]   = useState('')
  const [draft, setDraft]               = useState(null)       // { subject, body, review_flags, cp_tier_note }
  const [meta, setMeta]                 = useState(null)
  const [editedBody, setEditedBody]     = useState('')
  const [editedSubject, setEditedSubject] = useState('')
  const [to, setTo]                     = useState('')
  const [cc, setCc]                     = useState('')
  const [bcc, setBcc]                   = useState('')
  const [generating, setGenerating]     = useState(false)
  const [sending, setSending]           = useState(false)
  const [decisionLogId, setDecisionLogId] = useState(null)

  // Seed the recipient rows from the reply plan whenever the modal opens on a
  // message to answer. Done in an effect (not useState initialisers) because the
  // modal instance is often mounted before replyTo resolves.
  useEffect(() => {
    if (!isOpen || !replyPlan) return
    setTo(recipientsToEmails(replyPlan.to))
    setCc(recipientsToEmails(replyPlan.cc))
  }, [isOpen, replyPlan?.inReplyTo])

  // Auto-generate on open if we have enough context
  useEffect(() => {
    if (isOpen && context && contact && stage === 'input') {
      handleGenerate()
    }
  }, [isOpen])

  function reset() {
    setStage('input')
    setDraft(null)
    setMeta(null)
    setEditedBody('')
    setEditedSubject('')
    setTo('')
    setCc('')
    setBcc('')
    setExtra('')
    setDecisionLogId(null)
  }

  async function handleGenerate() {
    setGenerating(true)
    setStage('generating')
    try {
      const resp = await fetch('/api/janet-draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          draftType,
          voice:             voice || 'principal',
          principal:         effectivePrincipal,
          contact:           { name: contact?.name, email: contact?.email, cp_tier: contact?.va_cp_tier || contact?.cp_tier, id: contact?.id },
          additionalRecipients: additionalRecipients || [],
          isIntroduction:    draftType === 'introduction',
          context:           context || '',
          contextSource:     contextSource || null,
          subject:           replyTo?.subject || subject || '',
          // Replying in-thread changes how the email must read: no re-introduction,
          // answer the message that's already on the thread, keep the Re: subject.
          isReply,
          replyRecipients:   isReply
            ? { to: replyPlan.to, cc: replyPlan.cc }
            : null,
          dealContext:       dealContext || null,
          extraInstructions: extraInstructions || '',
          companyId:         'chatrone_us',
          userId:            user?.id || null,  // for calendar fetch in scheduling drafts
          offeredPersonName: offeredPersonName || null,
        }),
      })
      // Guard against Vercel returning plain-text error pages (timeouts, crashes)
      const rawText = await resp.text()
      let result
      try {
        result = JSON.parse(rawText)
      } catch {
        throw new Error(`Server error (${resp.status}) — ${rawText.slice(0, 120)}`)
      }
      if (!resp.ok) throw new Error(result.error || 'Draft failed')

      setDraft(result.draft)
      setMeta(result.meta)
      setEditedBody(result.draft.body || '')
      // On a reply the thread's own subject wins — a model-invented subject line
      // would read as a new conversation even though the message threads.
      setEditedSubject(replyPlan?.subject || result.draft.subject || subject || '')
      setStage('review')

      // Log to decision log
      try {
        const logId = await logJanetDecision({
          principalId:       user?.id,
          principalName:     effectivePrincipal.name,
          mode:              'external',
          authorityTier:     'T1',
          cpTier:            result.meta?.cpTier || 'unknown',
          recipientContactId: contact?.id || null,
          recipientEmail:    contact?.email || null,
          modelVersion:      result.meta?.modelUsed || 'claude-sonnet-5',
          inputContext:      { draftType, context: context?.slice(0, 500), subject },
          reasoningTrace:    result.draft?.tone_check || null,
          proposedAction:    { subject: result.draft?.subject, body: result.draft?.body?.slice(0, 500) },
          scannerResults:    { cp_tier: result.meta?.cpTier, review_flags: result.draft?.review_flags },
          finalAction:       'draft',
          companyId:         'chatrone_us',
        })
        setDecisionLogId(logId)
      } catch { /* log failure non-fatal */ }

    } catch (e) {
      toast.error(e.message || 'Janet could not generate a draft')
      setStage('input')
    } finally {
      setGenerating(false)
    }
  }

  // On a reply the To row is the reply-all set (editable); otherwise it's the
  // single contact the modal was opened for.
  const effectiveTo = isReply ? to.trim() : (contact?.email || '')

  async function handleSend() {
    if (!editedBody.trim()) { toast.warning('Draft is empty'); return }
    if (!effectiveTo) { toast.warning('No recipient'); return }
    setSending(true)
    try {
      // Get auth token so the API can verify the caller
      const { supabase: sb } = await import('../../lib/supabase')
      const { data: { session } } = await sb.auth.getSession()
      const token = session?.access_token

      const resp = await fetch('/api/send-gmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          to:              effectiveTo,
          subject:         editedSubject,
          html:            editedBody,
          ...(cc.trim()  ? { cc:  cc.trim()  } : {}),
          ...(bcc.trim() ? { bcc: bcc.trim() } : {}),
          ...(sendFromJanet ? { sendAsEmail: 'janet@chatrone.com' } : {}),
          // Threading: Gmail needs both — threadId to file it on the conversation,
          // inReplyTo for the In-Reply-To/References headers other clients read.
          ...(isReply ? { inReplyTo: replyPlan.inReplyTo, threadId: replyPlan.threadId } : {}),
          injectSignature: false,
        }),
      })
      const result = await resp.json()
      if (!resp.ok) throw new Error(result.error || 'Send failed')

      // Update decision log: draft → send
      if (decisionLogId) {
        try {
          await logJanetDecision({
            principalId:    user?.id,
            principalName:  effectivePrincipal.name,
            mode:           'external',
            authorityTier:  'T1',
            cpTier:         meta?.cpTier || 'unknown',
            recipientEmail: contact?.email,
            modelVersion:   meta?.modelUsed || 'claude-sonnet-5',
            inputContext:   { action: 'human_approved_and_sent', decisionLogId },
            proposedAction: { subject: editedSubject, body: editedBody.slice(0, 500) },
            scannerResults: {},
            finalAction:    'send',
            humanApproverId:   user?.id,
            humanApproverName: effectivePrincipal.name,
            companyId:         'chatrone_us',
          })
        } catch { /* non-fatal */ }
      }

      // Voice training: if the user edited Janet's draft before sending, log
      // the pair so future drafts trend toward the user's preferred language.
      // Mirrors api/janet-queue-decide.js edit_send write path.
      // Strip the appended signature from both sides so the training pair
      // captures only the body text the user actually edited.
      const originalBody = stripJanetSig(draft?.body || '')
      const finalBody = stripJanetSig(editedBody)
      if (originalBody && finalBody && originalBody !== finalBody) {
        // Classified evidence, recorded only for a send Gmail confirmed:
        // facts vs style, and a send AS Janet is not the sender's own voice
        // (api/janet-voice-evidence.js 'record').
        const sentMessageId = result?.gmail_message_id || result?.messageId
        if (sentMessageId) {
          try {
            const { getSessionWithTimeout } = await import('../../lib/authSession')
            const { data: { session } } = await getSessionWithTimeout(5000)
            await fetch('/api/janet-voice-evidence', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}) },
              body: JSON.stringify({ action: 'record', janetDraft: originalBody, sentHtml: finalBody, sentMessageId, emailType: draftType || null, sentAs: sendFromJanet ? 'janet@chatrone.com' : null }),
            })
          } catch { /* non-fatal */ }
        }
        try {
          const { supabase: sb } = await import('../../lib/supabase')
          await sb.from('va_decision_log').insert({
            user_id: user?.id,
            mode: 'external',
            authority_tier: 'T1',
            flow: 'janet_draft_modal_edit',
            original_body: originalBody,
            final_body: finalBody,
            final_action: 'edited_sent',
            edit_summary: `Edited ${draftType || 'draft'} before sending`,
            model_version: 'claude-sonnet-5',
          })
        } catch { /* non-fatal — schema mismatch on va_decision_log should not block training save */ }
      }

      toast.success(isReply ? 'Reply sent on the thread' : `Sent to ${contact?.name || contact?.email || effectiveTo}`)
      onSent?.()
      onClose()
      reset()
    } catch (e) {
      toast.error(e.message || 'Send failed')
    } finally {
      setSending(false)
    }
  }

  function handleEditInCompose() {
    // ComposeEmailModal expects to as a plain string and body as bodyHtml
    const toStr = isReply
      ? to.trim()
      : contact?.email
        ? (contact.name ? `${contact.name} <${contact.email}>` : contact.email)
        : ''
    openCompose({
      to: toStr,
      cc:  cc.trim()  || undefined,
      bcc: bcc.trim() || undefined,
      subject: editedSubject,
      bodyHtml: editedBody,
      // Hand the thread across intact — Compose sends with these headers, and the
      // quoted original gives the recipient the usual reply context.
      ...(isReply ? {
        inReplyTo:  replyPlan.inReplyTo,
        threadId:   replyPlan.threadId,
        quotedHtml: replyPlan.quotedHtml,
        emailIntelligenceId: replyTo?.emailIntelligenceId || undefined,
      } : {}),
      suppressSignature: true,
      ...(sendFromJanet ? { sendAsEmail: 'janet@chatrone.com' } : {}),
      fromAddress,
      isJanetDraft: true,
      // Voice training carrier — final body diffed against this at send time
      janetOriginalBody: draft?.body || '',
      janetDraftType:   draftType || null,
      janetCpTier:      contact?.va_cp_tier || contact?.cp_tier || null,
    })
    onClose()
    reset()
  }

  function handleDiscard() {
    if (decisionLogId) {
      logJanetDecision({
        principalId:   user?.id,
        principalName: effectivePrincipal.name,
        mode:          'external',
        authorityTier: 'T1',
        cpTier:        meta?.cpTier || 'unknown',
        inputContext:  { action: 'discarded', decisionLogId },
        proposedAction: {},
        scannerResults: {},
        finalAction:   'suppress',
        companyId:     'chatrone_us',
      }).catch(() => {})
    }
    onClose()
    reset()
  }

  const isCritical = meta?.isCritical || contact?.va_cp_tier === 'cp_1' || contact?.cp_tier === 'cp_1'

  return (
    <Modal isOpen={isOpen} onClose={handleDiscard} title="" size="lg">
      <div className="flex flex-col" style={{ minHeight: 400 }}>

        {/* Header */}
        <div className="flex items-center gap-3 px-6 pt-5 pb-4 border-b border-warm-200">
          <div className="w-8 h-8 rounded-full bg-navy-100 flex items-center justify-center shrink-0">
            <span className="text-sm font-bold text-navy-700">J</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-warm-900">
              Janet — {DRAFT_TYPE_LABELS[draftType] || 'Draft'}
            </p>
            <p className="text-xs text-warm-400">
              {voice === 'janet'
                ? <>On behalf of <span className="font-medium text-warm-600">{effectivePrincipal.name}</span></>
                : <>In <span className="font-medium text-warm-600">{effectivePrincipal.name}</span>'s voice</>}
              {contact?.name && <> → <span className="font-medium text-warm-600">{contact.name}</span></>}
            </p>
          </div>
          {isCritical && (
            <span className="shrink-0 flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200 text-[10px] font-medium">
              <Shield size={10} /> CP-1 — Review required
            </span>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 px-6 py-4">

          {/* Input stage */}
          {stage === 'input' && (
            <div className="space-y-4">
              <p className="text-sm text-warm-600">
                {draftType === 'introduction' && additionalRecipients?.length > 0
                  ? <>Janet will draft an introduction connecting <strong>{contact?.name}</strong> and <strong>{additionalRecipients.map(r => r.name).join(' & ')}</strong>. You'll review before it sends.</>
                  : <>Janet will draft a <strong>{DRAFT_TYPE_LABELS[draftType]}</strong> to <strong>{contact?.name || 'the recipient'}</strong>. You'll review before it sends.</>
                }
              </p>
              <div>
                <label className="block text-xs font-medium text-warm-600 mb-1.5">
                  Additional instructions <span className="text-warm-400">(optional)</span>
                </label>
                <textarea
                  value={extraInstructions}
                  onChange={e => setExtra(e.target.value)}
                  placeholder={`e.g. "Suggest Tuesday or Wednesday afternoon" or "Mention we spoke at the festival"`}
                  rows={3}
                  className="input-field w-full text-sm resize-none"
                />
              </div>
              <button onClick={handleGenerate} className="btn-primary gap-1.5">
                <Bot size={13} /> Generate Draft
              </button>
            </div>
          )}

          {/* Generating */}
          {stage === 'generating' && (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
              <Loader2 size={24} className="animate-spin text-navy-400" />
              <p className="text-sm text-warm-500">Janet is drafting…</p>
            </div>
          )}

          {/* Review stage */}
          {stage === 'review' && draft && (
            <JanetDraftReview
              draft={draft}
              isCritical={isCritical}
              headerSlot={isReply ? (
                <div className="flex items-start gap-2 p-2.5 rounded-lg bg-navy-50 border border-navy-100">
                  <CornerUpLeft size={12} className="text-navy-500 mt-0.5 shrink-0" />
                  <p className="text-[11px] text-navy-700 leading-relaxed">
                    Reply all on the existing thread
                    {replyTo?.subject ? <> — <span className="font-medium">{replyTo.subject}</span></> : null}
                    {replyTo?.fromName ? <span className="text-navy-500"> · last message from {replyTo.fromName}</span> : null}
                  </p>
                </div>
              ) : null}
              editedSubject={editedSubject}
              setEditedSubject={setEditedSubject}
              editedBody={editedBody}
              setEditedBody={setEditedBody}
              {...(isReply ? { to, setTo } : {})}
              cc={cc} setCc={setCc}
              bcc={bcc} setBcc={setBcc}
              extraInstructions={extraInstructions}
              setExtraInstructions={setExtra}
              onRegenerate={handleGenerate}
              generating={generating}
            />
          )}
        </div>

        {/* Footer actions */}
        {stage === 'review' && (
          <div className="shrink-0 flex items-center justify-between gap-3 px-6 py-4 border-t border-warm-200 bg-warm-50">
            <button onClick={handleDiscard} className="text-xs text-warm-400 hover:text-warm-600 transition-colors flex items-center gap-1">
              <X size={12} /> Discard
            </button>
            <div className="flex items-center gap-2">
              <button onClick={handleEditInCompose} className="btn-secondary gap-1.5 text-xs">
                <Edit2 size={12} /> Open in Compose
              </button>
              <button
                onClick={handleSend}
                disabled={sending || !effectiveTo}
                className="btn-primary gap-1.5 text-xs"
                title={!effectiveTo ? 'No email address for this contact' : ''}
              >
                {sending ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
                {isCritical ? 'Send (CP-1 — reviewed)' : isReply ? 'Send Reply All' : 'Send'}
              </button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  )
}
